import { useCallback, useMemo } from 'react';
import type { Editor, JSONContent } from '@tiptap/react';

export interface PageInfo {
  index: number;
  label: string;
  /** Index into doc.content marking where this page's content starts. */
  startContentIndex: number;
}

/**
 * Canvas ⇄ pages conversion (spec 046 US3, FR-028/FR-029/FR-031, research Decision 1).
 * Pages are `pageBreak` nodes living INSIDE the one TipTap document — conversion only
 * inserts/removes those nodes, so it is lossless by construction: no content is ever
 * copied, moved to a different store, or reordered.
 */
export function usePageView(getEditor: () => Editor | null) {
  const listPages = useCallback((doc: JSONContent | null): PageInfo[] => {
    if (!doc?.content) return [];
    const pages: PageInfo[] = [{ index: 0, label: 'Page 1', startContentIndex: 0 }];
    doc.content.forEach((node, i) => {
      if (node.type === 'pageBreak') {
        pages.push({
          index: pages.length,
          label: (node.attrs?.label as string) || `Page ${pages.length + 1}`,
          startContentIndex: i + 1,
        });
      }
    });
    return pages;
  }, []);

  /**
   * Inserts a pageBreak node at the current cursor position with the given label.
   * Media-atomicity (FR-029: "media elements land wholly on one page") holds structurally —
   * pageBreak and media nodes (mediaClip/videoEmbed/inkBlock) are all top-level block atoms,
   * so a block-level insert can only land between them as a sibling, never inside one.
   */
  const insertBreakAtCursor = useCallback((label: string) => {
    const editor = getEditor();
    if (!editor) return;
    editor.chain().focus().insertContent({ type: 'pageBreak', attrs: { label } }).run();
  }, [getEditor]);

  /** Removes ALL pageBreak nodes — canvas view. Every other node/attr is untouched (FR-029). */
  const convertToCanvas = useCallback(() => {
    const editor = getEditor();
    if (!editor) return;
    const { state } = editor;
    const positions: number[] = [];
    state.doc.descendants((node, pos) => {
      if (node.type.name === 'pageBreak') positions.push(pos);
    });
    if (positions.length === 0) return;
    const tr = state.tr;
    // Delete from the end so earlier positions stay valid.
    positions.slice().reverse().forEach(pos => {
      const node = tr.doc.nodeAt(pos);
      if (node) tr.delete(pos, pos + node.nodeSize);
    });
    editor.view.dispatch(tr);
  }, [getEditor]);

  const getDoc = useCallback((): JSONContent | null => {
    const editor = getEditor();
    return editor ? editor.getJSON() : null;
  }, [getEditor]);

  return { listPages, insertBreakAtCursor, convertToCanvas, getDoc };
}
